import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Image,
  SafeAreaView,
  Modal,
  Platform,
  Dimensions,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTheme } from '../screens/ThemeContext';

const { width } = Dimensions.get('window');

// Composant de calendrier web compatible
const WebCalendar = ({ current, onDayPress, theme, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(current));
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Ajouter les jours du mois précédent
    for (let i = 0; i < startingDay; i++) {
      const prevDate = new Date(year, month, -startingDay + i + 1);
      days.push({
        date: prevDate,
        day: prevDate.getDate(),
        disabled: true
      });
    }
    
    // Ajouter les jours du mois actuel
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(year, month, i);
      days.push({
        date: currentDate,
        day: i,
        disabled: false
      });
    }
    
    // Ajouter les jours du mois suivant
    const remainingDays = 42 - days.length; // 6 semaines * 7 jours
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({
        date: nextDate,
        day: nextDate.getDate(),
        disabled: true
      });
    }
    
    return days;
  };

  const handleDayPress = (day) => {
    if (!day.disabled) {
      onDayPress({ timestamp: day.date.getTime() });
    }
  };

  const changeMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const days = getDaysInMonth(currentMonth);
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  return (
    <View style={styles.webCalendarContainer}>
      <View style={styles.webCalendarHeader}>
        <TouchableOpacity onPress={() => changeMonth(-1)} style={styles.webCalendarArrow}>
          <MaterialCommunityIcons name="chevron-left" size={24} color={theme.primary} />
        </TouchableOpacity>
        <Text style={[styles.webCalendarTitle, { color: theme.text }]}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </Text>
        <TouchableOpacity onPress={() => changeMonth(1)} style={styles.webCalendarArrow}>
          <MaterialCommunityIcons name="chevron-right" size={24} color={theme.primary} />
        </TouchableOpacity>
      </View>
      
      <View style={styles.webCalendarDaysHeader}>
        {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map(day => (
          <Text key={day} style={[styles.webCalendarDayHeader, { color: theme.textSecondary }]}>
            {day}
          </Text>
        ))}
      </View>
      
      <View style={styles.webCalendarGrid}>
        {days.map((day, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.webCalendarDay,
              day.disabled && styles.webCalendarDayDisabled,
              { backgroundColor: theme.surface }
            ]}
            onPress={() => handleDayPress(day)}
            disabled={day.disabled}
          >
            <Text style={[
              styles.webCalendarDayText,
              { color: day.disabled ? theme.textSecondary : theme.text }
            ]}>
              {day.day}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
      
      <TouchableOpacity style={[styles.webCalendarCloseButton, { backgroundColor: theme.primary }]} onPress={onClose}>
        <Text style={styles.webCalendarCloseButtonText}>Fermer</Text>
      </TouchableOpacity>
    </View>
  );
};

export default function CreateContentScreen({ navigation }) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    image: null,
    concours: [''],
    type: 'Présentiel',
    duree: '',
    dateDebut: new Date(),
    dateFin: new Date(),
    avantages: [
      { titre: '', description: '' }
    ],
    contact: {
      telephone: '',
      whatsapp: '',
      email: '',
      adresse: ''
    }
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateType, setDateType] = useState(null);

  const pickImage = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      // aspect: [16, 9],
      quality: 1,
    });

    if (!result.canceled) {
      setFormData(prev => ({
        ...prev,
        image: result.assets[0].uri
      }));
    }
  };

  const addConcours = () => {
    setFormData(prev => ({
      ...prev,
      concours: [...prev.concours, '']
    }));
  };

  const updateConcours = (index, value) => {
    setFormData(prev => {
      const newConcours = [...prev.concours];
      newConcours[index] = value;
      return { ...prev, concours: newConcours };
    });
  };

  const removeConcours = (index) => {
    setFormData(prev => ({
      ...prev,
      concours: prev.concours.filter((_, i) => i !== index)
    }));
  };

  const addAvantage = () => {
    setFormData(prev => ({
      ...prev,
      avantages: [...prev.avantages, { titre: '', description: '' }]
    }));
  };

  const removeAvantage = (index) => {
    if (formData.avantages.length > 1) {
      setFormData(prev => ({
        ...prev,
        avantages: prev.avantages.filter((_, i) => i !== index)
      }));
    }
  };

  const updateAvantage = (index, field, value) => {
    setFormData(prev => {
      const avantages = [...prev.avantages];
      avantages[index] = { ...avantages[index], [field]: value };
      return { ...prev, avantages };
    });
  };

  const handleSubmit = async () => {
    if (!formValid || loading) return;
  
    try {
      setLoading(true);
  
      // Vérifier les données du centre
      if (!user?.uid) {
        Alert.alert('Erreur', 'Utilisateur non authentifié');
        return;
      }
  
      const imageUrl = await uploadImage(formData.image);
  
      const announcementData = {
        titre: formData.titre,
        description: formData.description,
        imageUrl,
        concours: formData.concours.filter(c => c.trim()),
        dateDebut: formData.dateDebut,
        dateFin: formData.dateFin,
        avantages: formData.avantages.filter(a => a.titre.trim() && a.description.trim()),
        contact: formData.contact,
        centerInfo: {
          centerId: user.uid,
          centerName: centerData?.centerName || 'Non spécifié',
          centerAddress: centerData?.address || 'Non spécifié',
        }
      };
  
      // Au lieu de publier directement, naviguer vers l'écran de paiement
      navigation.navigate('AnnouncementPaymentScreen', {
        announcementData
      });
  
    } catch (error) {
      console.error('Error preparing announcement:', error);
      Alert.alert('Erreur', 'Une erreur est survenue lors de la préparation de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  const styles = StyleSheet.create({
    safeArea: {
      flex: 1,
      backgroundColor: theme.primary,
    },
    container: {
      flex: 1,
      backgroundColor: theme.background,
    },
    header: {
      paddingTop: Platform.OS === 'ios' ? 50 : 20,
      paddingBottom: 30,
      paddingHorizontal: 20,
      borderBottomLeftRadius: 30,
      borderBottomRightRadius: 30,
    },
    headerTitle: {
        color: '#fff',
        fontSize: 24,
        fontWeight: 'bold',
    },
    content: {
      flex: 1,
      padding: 20,
      paddingTop: 10,
    },
    section: {
      marginBottom: 20,
      backgroundColor: theme.surface,
      borderRadius: 16,
      padding: 16,
      borderWidth: 1,
      borderColor: theme.border,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.text,
      marginBottom: 15,
      letterSpacing: 0.3,
    },
    imageContainer: {
      height: 200,
      backgroundColor: theme.surface,
      borderRadius: 16,
      marginTop: -25,
      overflow: 'hidden',
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: 20,
      borderWidth: 2,
      borderColor: theme.primary + '30',
      borderStyle: 'dashed',
    },
    previewImage: {
      width: '100%',
      height: '100%',
      borderRadius: 16,
    },
    uploadText: {
      color: theme.textSecondary,
      marginTop: 10,
      fontSize: 14,
    },
    input: {
      backgroundColor: theme.background,
      borderRadius: 12,
      padding: 15,
      color: theme.text,
      marginBottom: 15,
      fontSize: 16,
      borderWidth: 1,
      borderColor: theme.border,
    },
    textArea: {
      height: 120,
      textAlignVertical: 'top',
      paddingTop: 15,
    },
    concoursContainer: {
      marginBottom: 10,
    },
    concoursInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
      backgroundColor: theme.background,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.border,
      paddingRight: 8,
    },
    concoursInput: {
      flex: 1,
      padding: 15,
      color: theme.text,
      fontSize: 16,
    },
    iconButton: {
      padding: 8,
      borderRadius: 10,
      backgroundColor: theme.primary + '15',
    },
    addButton: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 15,
      borderRadius: 12,
      backgroundColor: theme.primary + '15',
      justifyContent: 'center',
    },
    addButtonText: {
      color: theme.primary,
      marginLeft: 8,
      fontWeight: '600',
      fontSize: 16,
    },
    typeSelector: {
      flexDirection: 'row',
      backgroundColor: theme.background,
      borderRadius: 12,
      padding: 4,
      borderWidth: 1,
      borderColor: theme.border,
    },
    typeOption: {
      flex: 1,
      paddingVertical: 12,
      alignItems: 'center',
      borderRadius: 8,
    },
    typeOptionText: {
      fontSize: 15,
      fontWeight: '600',
    },
    dateInput: {
      backgroundColor: theme.background,
      borderRadius: 12,
      padding: 15,
      marginBottom: 15,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderWidth: 1,
      borderColor: theme.border,
    },
    dateText: {
      fontSize: 16,
      color: theme.text,
    },
    calendarContainer: {
      backgroundColor: theme.surface,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      padding: 20,
    },
    calendar: {
      borderRadius: 16,
      elevation: 4,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      backgroundColor: theme.background,
      padding: 10,
    },
    avantageCard: {
      backgroundColor: theme.background,
      borderRadius: 12,
      padding: 16,
      marginBottom: 15,
      borderWidth: 1,
      borderColor: theme.border,
    },
    avantageHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 20,
    },
    avantageNumberContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    avantageNumber: {
      backgroundColor: theme.primary,
      width: 24,
      height: 24,
      borderRadius: 12,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 8,
    },
    avantageNumberText: {
      color: '#fff',
      fontWeight: '600',
      fontSize: 14,
    },
    avantageTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.text,
    },
    contactInput: {
      backgroundColor: theme.background,
      borderRadius: 12,
      padding: 15,
      color: theme.text,
      fontSize: 16,
      marginBottom: 15,
      borderWidth: 1,
      borderColor: theme.border,
      flexDirection: 'row',
      alignItems: 'center',
    },
    contactIcon: {
      marginRight: 10,
      width: 24,
    },
    submitButton: {
      backgroundColor: theme.primary,
      borderRadius: 12,
      padding: 18,
      alignItems: 'center',
      marginTop: 30,
      marginBottom: 30,
      shadowColor: theme.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 5,
    },
    submitText: {
      color: '#fff',
      fontSize: 18,
      fontWeight: '600',
      letterSpacing: 0.5,
    },
    bottomSpace: {
        height: 100,
    },
    webCalendarContainer: {
      backgroundColor: theme.surface,
      borderRadius: 16,
      padding: 20,
      marginTop: 20,
      alignItems: 'center',
    },
    webCalendarHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      marginBottom: 15,
    },
    webCalendarArrow: {
      padding: 10,
    },
    webCalendarTitle: {
      fontSize: 18,
      fontWeight: 'bold',
    },
    webCalendarDaysHeader: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      width: '100%',
      marginBottom: 10,
    },
    webCalendarDayHeader: {
      fontSize: 14,
    },
    webCalendarGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-around',
      width: '100%',
    },
    webCalendarDay: {
      width: '14.28%', // 1/7 of width
      aspectRatio: 1,
      justifyContent: 'center',
      alignItems: 'center',
      marginVertical: 5,
      borderRadius: 8,
    },
    webCalendarDayDisabled: {
      opacity: 0.5,
    },
    webCalendarDayText: {
      fontSize: 14,
      fontWeight: '600',
    },
    webCalendarCloseButton: {
      backgroundColor: theme.primary,
      borderRadius: 12,
      paddingVertical: 12,
      paddingHorizontal: 25,
      marginTop: 20,
    },
    webCalendarCloseButtonText: {
      color: '#fff',
      fontSize: 16,
      fontWeight: '600',
    },
  });

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor={theme.primary} />
      
      <ScrollView style={styles.container}>
        <LinearGradient
          colors={[theme.primary, theme.primaryDark]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
          style={styles.header}
        >
          <Text style={styles.headerTitle}>Créer une annonce</Text>
        </LinearGradient>

        <View style={styles.content}>
          {/* Image */}
          <TouchableOpacity style={styles.imageContainer} onPress={pickImage}>
            {formData.image ? (
              <Image source={{ uri: formData.image }} style={styles.previewImage} />
            ) : (
              <MaterialCommunityIcons name="image-plus" size={40} color={theme.textSecondary} />
            )}
          </TouchableOpacity>

          {/* Titre */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Titre de l'annonce</Text>
            <TextInput
              style={styles.input}
              value={formData.titre}
              onChangeText={(text) => setFormData(prev => ({...prev, titre: text}))}
              placeholder="Entrez le titre"
              placeholderTextColor={theme.textSecondary}
            />
          </View>

          {/* Description */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Description</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.description}
              onChangeText={(text) => setFormData(prev => ({...prev, description: text}))}
              placeholder="Décrivez votre programme"
              placeholderTextColor={theme.textSecondary}
              multiline
              numberOfLines={4}
            />
          </View>

          {/* Concours préparés */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Concours préparés</Text>
            {formData.concours.map((concours, index) => (
              <View key={index} style={styles.concoursInputContainer}>
                <TextInput
                  style={styles.concoursInput}
                  value={concours}
                  onChangeText={(text) => updateConcours(index, text)}
                  placeholder="Nom du concours"
                  placeholderTextColor={theme.textSecondary}
                />
                {formData.concours.length > 1 && (
                  <TouchableOpacity 
                    style={styles.iconButton}
                    onPress={() => removeConcours(index)}
                  >
                    <MaterialCommunityIcons 
                      name="minus-circle" 
                      size={24} 
                      color={theme.primary} 
                    />
                  </TouchableOpacity>
                )}
              </View>
            ))}
            <TouchableOpacity style={styles.addButton} onPress={addConcours}>
              <MaterialCommunityIcons name="plus" size={24} color={theme.primary} />
              <Text style={styles.addButtonText}>Ajouter un concours</Text>
            </TouchableOpacity>
          </View>

          {/* Type de formation */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Type de formation</Text>
            <View style={styles.typeSelector}>
              {['Présentiel', 'En ligne', 'Hybride'].map((type, index) => (
                <TouchableOpacity
                  key={index}
                  style={[
                    styles.typeOption,
                    {
                      backgroundColor: formData.type === type
                        ? theme.primary
                        : 'transparent'
                    }
                  ]}
                  onPress={() => setFormData(prev => ({...prev, type}))}
                >
                  <Text style={{
                    color: formData.type === type ? '#fff' : theme.textSecondary
                  }}>
                    {type}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Durée et Dates */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Durée et dates</Text>
            {Platform.OS === 'web' ? (
              <>
                <label>Date de début :</label>
                <input
                  type="date"
                  value={formData.dateDebut ? formData.dateDebut.toISOString().substr(0, 10) : ''}
                  onChange={e => setFormData(prev => ({ ...prev, dateDebut: new Date(e.target.value) }))}
                  style={{ padding: 8, fontSize: 16 }}
                />
                <label>Date de fin :</label>
                <input
                  type="date"
                  value={formData.dateFin ? formData.dateFin.toISOString().substr(0, 10) : ''}
                  onChange={e => setFormData(prev => ({ ...prev, dateFin: new Date(e.target.value) }))}
                  style={{ padding: 8, fontSize: 16 }}
                />
              </>
            ) : (
              <>
                <TextInput
                  value={formData.dateDebut ? formData.dateDebut.toISOString().substr(0, 10) : ''}
                  editable={false}
                  style={{ padding: 8, fontSize: 16, backgroundColor: '#eee' }}
                />
                <TextInput
                  value={formData.dateFin ? formData.dateFin.toISOString().substr(0, 10) : ''}
                  editable={false}
                  style={{ padding: 8, fontSize: 16, backgroundColor: '#eee' }}
                />
              </>
            )}
          </View>

          {/* Modal pour le calendrier */}
          <Modal
            visible={showDatePicker}
            transparent={true}
            animationType="slide"
          >
            <View style={styles.modalOverlay}>
              <View style={styles.calendarContainer}>
                <WebCalendar
                  current={dateType === 'debut' ? formData.dateDebut.toISOString() : formData.dateFin.toISOString()}
                  onDayPress={(day) => {
                    const selectedDate = new Date(day.timestamp);
                    setFormData(prev => ({
                      ...prev,
                      [dateType === 'debut' ? 'dateDebut' : 'dateFin']: selectedDate
                    }));
                    setShowDatePicker(false);
                  }}
                  theme={{
                    backgroundColor: theme.surface,
                    text: theme.text,
                    textSecondary: theme.textSecondary,
                    primary: theme.primary,
                    surface: theme.surface,
                    border: theme.border,
                  }}
                  onClose={() => setShowDatePicker(false)}
                />
              </View>
            </View>
          </Modal>

          {/* Avantages */}
          <View style={styles.section}>
            <View style={styles.avantageHeader}>
            <Text style={styles.sectionTitle}>Pourquoi nous choisir ?</Text>
              <TouchableOpacity 
                style={styles.iconButton}
                onPress={addAvantage}
              >
                <MaterialCommunityIcons 
                  name="plus-circle" 
                  size={24} 
                  color={theme.primary} 
                />
              </TouchableOpacity>
            </View>

            {formData.avantages.map((avantage, index) => (
              <View key={index} style={styles.avantageCard}>
                <View style={{ 
                  flexDirection: 'row', 
                  justifyContent: 'space-between',
                  marginBottom: 10
                }}>
                  <Text style={[styles.sectionTitle, { fontSize: 16 }]}>
                    Avantage {index + 1}
                  </Text>
                  {formData.avantages.length > 1 && (
                    <TouchableOpacity
                      onPress={() => removeAvantage(index)}
                    >
                      <MaterialCommunityIcons 
                        name="delete" 
                        size={24} 
                        color={theme.textSecondary} 
                      />
                    </TouchableOpacity>
                  )}
                </View>
                <TextInput
                  style={[styles.input, { marginBottom: 10 }]}
                  value={avantage.titre}
                  onChangeText={(text) => updateAvantage(index, 'titre', text)}
                  placeholder="Titre de l'avantage"
                  placeholderTextColor={theme.textSecondary}
                />
                <TextInput
                  style={[styles.input, styles.textArea, { marginBottom: 0 }]}
                  value={avantage.description}
                  onChangeText={(text) => updateAvantage(index, 'description', text)}
                  placeholder="Description de l'avantage"
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={3}
                />
              </View>
            ))}
          </View>

          {/* Contact */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Contact</Text>
            <View style={styles.contactInput}>
              <MaterialCommunityIcons 
                name="phone" 
                size={20} 
                color={theme.primary}
                style={styles.contactIcon}
              />
              <TextInput
                style={{ flex: 1, color: theme.text }}
                value={formData.contact.telephone}
                onChangeText={(text) => setFormData(prev => ({
                  ...prev,
                  contact: { ...prev.contact, telephone: text }
                }))}
                placeholder="Numéro de téléphone"
                placeholderTextColor={theme.textSecondary}
                keyboardType="phone-pad"
              />
            </View>
            
            <View style={styles.contactInput}>
              <MaterialCommunityIcons 
                name="whatsapp" 
                size={20} 
                color={theme.primary}
                style={styles.contactIcon}
              />
              <TextInput
                style={{ flex: 1, color: theme.text }}
                value={formData.contact.whatsapp}
                onChangeText={(text) => setFormData(prev => ({
                  ...prev,
                  contact: { ...prev.contact, whatsapp: text }
                }))}
                placeholder="Numéro WhatsApp"
                placeholderTextColor={theme.textSecondary}
                keyboardType="phone-pad"
              />
            </View>
            
            <View style={styles.contactInput}>
              <MaterialCommunityIcons 
                name="email" 
                size={20} 
                color={theme.primary}
                style={styles.contactIcon}
              />
              <TextInput
                style={{ flex: 1, color: theme.text }}
                value={formData.contact.email}
                onChangeText={(text) => setFormData(prev => ({
                  ...prev,
                  contact: { ...prev.contact, email: text }
                }))}
                placeholder="Adresse email"
                placeholderTextColor={theme.textSecondary}
                keyboardType="email-address"
              />
            </View>
            
            <View style={[styles.contactInput, styles.textArea]}>
              <MaterialCommunityIcons 
                name="map-marker" 
                size={20} 
                color={theme.primary}
                style={[styles.contactIcon, { marginTop: 3 }]}
              />
              <TextInput
                style={{ flex: 1, color: theme.text }}
                value={formData.contact.adresse}
                onChangeText={(text) => setFormData(prev => ({
                  ...prev,
                  contact: { ...prev.contact, adresse: text }
                }))}
                placeholder="Adresse physique"
                placeholderTextColor={theme.textSecondary}
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          {/* Submit Button */}
          <TouchableOpacity style={styles.submitButton} onPress={handleSubmit}>
            <Text style={styles.submitText}>Publier l'annonce</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.bottomSpace} />
      </ScrollView>
    </SafeAreaView>
  );
} 